'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

import styles from './AteliersActivitiesPage.module.scss';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { trackMetaBookingEntry } from '@/src/lib/meta/meta-pixel';

type BookingProduct = {
    _id: string;
    title: string;
    slug: string;
    type?: string;
    isActive?: boolean;
    ageMin?: number;
    durationMin?: number;
    capacityPerSlot?: number;
    publicDescription?: string;
    imageUrl?: string;
    included?: string[];
    pricing?: {
        mode?: 'fixed' | 'variants';
        price?: number;
        variants?: Array<{
            id: string;
            label: string;
            price: number;
        }>;
        options?: Array<{
            id: string;
            label: string;
            price: number;
        }>;
    };
    schedule?: Array<{
        weekday: number;
        isClosed?: boolean;
        slots?: Array<{ startTime: string; endTime: string }>;
    }>;
};

type CrudResponse<T> = {
    items: T[];
    total: number;
    page: number;
    perPage: number;
};

type ActivityOverride = {
    eyebrow?: string;
    title?: string;
    intro?: string;
    paragraphs?: string[];
    included?: string[];
    usefulInfo?: string[];
    highlightTitle?: string;
    highlightText?: string;
    imageUrl?: string;
    imageAlt?: string;
    ctaLabel?: string;
    badge?: string;
};

export type AteliersActivitiesSlice = {
    type: 'ateliers-activities';
    anchor?: string;
    eyebrow?: string;
    title?: string;
    description?: string;
    indexTitle?: string;
    discoverButtonLabel?: string;
    bookingButtonLabel?: string;
    bookingButtonLabelWithTitle?: string;
    backToTopLabel?: string;
    scheduleTitle?: string;
    scheduleFallback?: string;
    usefulInfoTitle?: string;
    usefulInfoBookingLinkText?: string;
    usefulInfoDirectAccessLabel?: string;
    usefulInfoClosedDatesLabel?: string;
    includedLabel?: string;
    openDaysReservationFallback?: string;
    workshopPrefix?: string;
    startingFromLabel?: string;
    ageLabel?: string;
    durationLabel?: string;
    capacityLabel?: string;
    priceLabel?: string;
    yearsSuffix?: string;
    minutesSuffix?: string;
    peopleSuffix?: string;
    errorText?: string;
    emptyText?: string;
    infoPillFallback?: string;
    placeholderImage?: string;
    bookingBaseHref?: string;
    contentOverrides?: Record<string, ActivityOverride>;
};

type Props = {
    slice: AteliersActivitiesSlice;
    lang?: 'fr' | 'en';
};

const WEEKDAYS_FR = [
    'Dimanche',
    'Lundi',
    'Mardi',
    'Mercredi',
    'Jeudi',
    'Vendredi',
    'Samedi',
];

function cleanText(value?: string) {
    return (value ?? '').replace(/\s+/g, ' ').trim();
}

function slugify(value?: string) {
    return (value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

function formatPriceEuros(value?: number | null) {
    if (typeof value !== 'number' || Number.isNaN(value)) return null;
    return `${value}€`;
}

function getBookingStartingPrice(product: BookingProduct): number | null {
    if (!product.pricing) return null;

    if (product.pricing.mode === 'fixed' && typeof product.pricing.price === 'number') {
        return product.pricing.price;
    }

    if (
        product.pricing.mode === 'variants' &&
        Array.isArray(product.pricing.variants) &&
        product.pricing.variants.length
    ) {
        const prices = product.pricing.variants
            .map((variant) => variant.price)
            .filter((price) => typeof price === 'number');

        if (!prices.length) return null;
        return Math.min(...prices);
    }

    return null;
}

function getSchedulePreview(
    schedule?: BookingProduct['schedule'],
    fallback = 'Créneaux communiqués sur réservation.',
) {
    if (!Array.isArray(schedule) || !schedule.length) {
        return [];
    }

    const openDays = schedule
        .filter((day) => !day.isClosed && Array.isArray(day.slots) && day.slots.length > 0)
        .map((day) => ({
            weekday: day.weekday,
            title: WEEKDAYS_FR[day.weekday] ?? `Jour ${day.weekday}`,
            slots: (day.slots ?? []).slice(0, 4),
        }));

    if (!openDays.length) {
        return [
            {
                title: 'Horaires',
                slots: [{ startTime: fallback, endTime: '' }],
            },
        ];
    }

    return openDays;
}

function buildClosedDaysText(schedule?: BookingProduct['schedule']) {
    if (!Array.isArray(schedule) || !schedule.length) return null;

    const closedDays = schedule
        .filter((day) => day.isClosed || !day.slots || day.slots.length === 0)
        .map((day) => WEEKDAYS_FR[day.weekday] ?? null)
        .filter(Boolean);

    if (!closedDays.length) return null;
    return closedDays.join(', ');
}

function replaceTitleTemplate(template: string | undefined, title: string, fallback: string) {
    const safeTemplate = template || fallback;
    return safeTemplate.replace('{{title}}', title);
}

async function fetchAllPages<T>(collection: string): Promise<T[]> {
    const results: T[] = [];
    let page = 1;
    let totalPages = 1;

    do {
        const res = await fetch(`/api/admin/crud/${collection}?page=${page}`, {
            method: 'GET',
            credentials: 'include',
            cache: 'no-store',
        });

        if (!res.ok) {
            throw new Error(`Failed to fetch collection "${collection}"`);
        }

        const data: CrudResponse<T> = await res.json();
        results.push(...(data.items ?? []));
        totalPages = Math.max(1, Math.ceil((data.total ?? 0) / (data.perPage || 20)));
        page += 1;
    } while (page <= totalPages);

    return results;
}

function getOverrideForProduct(
    overrides: Record<string, ActivityOverride> | undefined,
    product: BookingProduct,
) {
    if (!overrides) return undefined;

    const bySlug = overrides[product.slug];
    if (bySlug) return bySlug;

    const byId = overrides[product._id];
    if (byId) return byId;

    const byTitleSlug = overrides[slugify(product.title)];
    if (byTitleSlug) return byTitleSlug;

    return undefined;
}

export default function AteliersActivities({ slice, lang = 'fr' }: Props) {
    const dictionary = useDictionary();

    const [products, setProducts] = useState<BookingProduct[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        let cancelled = false;

        async function loadProducts() {
            try {
                setLoading(true);
                setHasError(false);

                const allProducts = await fetchAllPages<BookingProduct>('booking_products');
                if (cancelled) return;

                const activeProducts = allProducts
                    .filter((product) => product.isActive !== false)
                    .sort((a, b) => a.title.localeCompare(b.title, 'fr'));

                setProducts(activeProducts);
            } catch (error) {
                console.error('AteliersActivities fetch error:', error);
                if (!cancelled) {
                    setHasError(true);
                    setProducts([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        loadProducts();

        return () => {
            cancelled = true;
        };
    }, []);

    const labels = useMemo(() => {
        return {
            eyebrow: slice.eyebrow ?? '',
            title: slice.title ?? '',
            description: slice.description ?? '',
            indexTitle: slice.indexTitle ?? 'Index',
            discoverButtonLabel: slice.discoverButtonLabel ?? 'Découvrir les ateliers',
            bookingButtonLabel: slice.bookingButtonLabel ?? dictionary?.general?.book ?? 'Réserver',
            bookingButtonLabelWithTitle:
                slice.bookingButtonLabelWithTitle ?? 'Réserver {{title}}',
            backToTopLabel: slice.backToTopLabel ?? 'Revenir en haut',
            scheduleTitle: slice.scheduleTitle ?? 'Créneaux habituels',
            scheduleFallback: slice.scheduleFallback ?? 'Créneaux communiqués sur réservation.',
            usefulInfoTitle: slice.usefulInfoTitle ?? 'Infos utiles',
            usefulInfoBookingLinkText:
                slice.usefulInfoBookingLinkText ??
                'Le lien de réservation ouvre la page dédiée à l’activité.',
            usefulInfoDirectAccessLabel:
                slice.usefulInfoDirectAccessLabel ?? 'Accès direct à cet atelier :',
            usefulInfoClosedDatesLabel:
                slice.usefulInfoClosedDatesLabel ?? 'Jours fermés :',
            includedLabel: slice.includedLabel ?? 'Inclus',
            openDaysReservationFallback:
                slice.openDaysReservationFallback ?? 'Sur réservation',
            workshopPrefix: slice.workshopPrefix ?? 'Atelier',
            startingFromLabel: slice.startingFromLabel ?? 'À partir de',
            ageLabel: slice.ageLabel ?? 'Âge',
            durationLabel: slice.durationLabel ?? 'Durée',
            capacityLabel: slice.capacityLabel ?? 'Disponibilité',
            priceLabel: slice.priceLabel ?? 'Tarif',
            yearsSuffix: slice.yearsSuffix ?? 'ans',
            minutesSuffix: slice.minutesSuffix ?? 'min',
            peopleSuffix: slice.peopleSuffix ?? 'pers.',
            errorText:
                slice.errorText ?? 'Impossible de charger les ateliers pour le moment.',
            emptyText: slice.emptyText ?? 'Aucun atelier disponible pour le moment.',
            infoPillFallback: slice.infoPillFallback ?? '',
            placeholderImage:
                slice.placeholderImage ?? dictionary?.general?.imageDefault ?? '/images/placeholder.jpg',
        };
    }, [slice, dictionary]);

    const items = useMemo(() => {
        return products.map((product) => {
            const override = getOverrideForProduct(slice.contentOverrides, product);
            const price = getBookingStartingPrice(product);
            const schedule = getSchedulePreview(product.schedule, labels.scheduleFallback);
            const closedDays = buildClosedDaysText(product.schedule);

            const editorialParagraphs =
                override?.paragraphs && override.paragraphs.length
                    ? override.paragraphs
                    : cleanText(product.publicDescription)
                        ? [cleanText(product.publicDescription)]
                        : [];

            const intro =
                override?.intro ||
                cleanText(product.publicDescription) ||
                editorialParagraphs[0] ||
                '';

            return {
                product,
                override,
                price,
                schedule,
                closedDays,
                anchorId: product.slug || slugify(product.title) || product._id,
                title: override?.title || product.title,
                eyebrow: override?.eyebrow || `${labels.workshopPrefix}`,
                intro,
                paragraphs: editorialParagraphs,
                included: override?.included ?? product.included ?? [],
                usefulInfo: override?.usefulInfo ?? [],
                imageUrl: override?.imageUrl || product.imageUrl || labels.placeholderImage,
                imageAlt: override?.imageAlt || product.title,
                bookingHref: '/booking/atelier',
                availabilityText: 'Week-end',
            };
        });
    }, [products, slice.contentOverrides, labels]);

    useEffect(() => {
        if (loading || !items.length) return;

        const hash = window.location.hash;
        if (!hash) return;

        const targetId = decodeURIComponent(hash.replace('#', ''));
        if (!targetId) return;

        let attempts = 0;
        const maxAttempts = 12;
        let timeoutId: number | undefined;

        const scrollToAnchor = () => {
            const element = document.getElementById(targetId);
            if (element) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
                return;
            }

            attempts += 1;
            if (attempts < maxAttempts) {
                timeoutId = window.setTimeout(scrollToAnchor, 180);
            }
        };

        timeoutId = window.setTimeout(scrollToAnchor, 120);

        return () => {
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [loading, items]);

    if (loading) {
        return (
            <section className={styles.page} id={slice.anchor || 'ateliers-activities'}>
                <div className={styles.bodyWrap}>
                    <aside className={styles.sidebar}>
                        <div className={`${styles.sidebarBox} ${styles.sideSkeleton}`} />
                    </aside>

                    <div className={styles.contentSkeleton}>
                        {Array.from({ length: 3 }).map((_, index) => (
                            <div key={index} className={styles.cardSkeleton} />
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    if (hasError) {
        return (
            <section className={styles.page} id={slice.anchor || 'ateliers-activities'}>
                <div className={styles.stateBlock}>
                    <p>{labels.errorText}</p>
                </div>
            </section>
        );
    }

    if (!items.length) {
        return (
            <section className={styles.page} id={slice.anchor || 'ateliers-activities'}>
                <div className={styles.stateBlock}>
                    <p>{labels.emptyText}</p>
                </div>
            </section>
        );
    }

    return (
        <section className={styles.page} id={slice.anchor || 'ateliers-activities'}>
            <div className={styles.bodyWrap}>
                <aside className={styles.sidebar}>
                    <div className={styles.sidebarBox}>
                        <p className={styles.sidebarLabel}>{labels.indexTitle}</p>
                        <nav className={styles.anchorNav} aria-label={labels.indexTitle}>
                            {items.map((item) => (
                                <a key={item.anchorId} href={`#${item.anchorId}`} className={styles.anchorLink}>
                                    {item.title}
                                </a>
                            ))}
                        </nav>
                    </div>
                </aside>

                <div className={styles.content} id="activities-list">
                    {items.map((item) => (
                        <article
                            key={item.product._id}
                            id={item.anchorId}
                            className={styles.activitySection}
                        >
                            <div className={styles.activityTop}>
                                <div className={styles.activityImageWrap}>
                                    <Image
                                        src={item.imageUrl}
                                        alt={item.imageAlt}
                                        fill
                                        className={styles.activityImage}
                                        sizes="(max-width: 1100px) 100vw, 55vw"
                                    />
                                </div>

                                <div className={styles.activitySummary}>
                                    <p className={styles.activityKicker}>
                                        {item.override?.badge || item.eyebrow}
                                    </p>

                                    <h3 className={styles.activityTitle}>{item.title}</h3>

                                    <div className={styles.metaGrid}>
                                        <div className={styles.metaItem}>
                                            <span className={styles.metaLabel}>{labels.ageLabel}</span>
                                            <span className={styles.metaValue}>
                                                {typeof item.product.ageMin === 'number'
                                                    ? `${item.product.ageMin} ${labels.yearsSuffix}`
                                                    : labels.openDaysReservationFallback}
                                            </span>
                                        </div>

                                        <div className={styles.metaItem}>
                                            <span className={styles.metaLabel}>{labels.durationLabel}</span>
                                            <span className={styles.metaValue}>
                                                {typeof item.product.durationMin === 'number'
                                                    ? `${item.product.durationMin} ${labels.minutesSuffix}`
                                                    : labels.openDaysReservationFallback}
                                            </span>
                                        </div>

                                        <div className={styles.metaItem}>
                                            <span className={styles.metaLabel}>{labels.capacityLabel}</span>
                                            <span className={styles.metaValue}>
                                                {item.availabilityText}
                                            </span>
                                        </div>

                                        <div className={styles.metaItem}>
                                            <span className={styles.metaLabel}>{labels.priceLabel}</span>
                                            <span className={styles.metaValue}>
                                                {item.price !== null
                                                    ? `${labels.startingFromLabel} ${formatPriceEuros(item.price)}`
                                                    : dictionary?.general?.priceOnDemand || 'Tarif sur devis'}
                                            </span>
                                        </div>
                                    </div>

                                    {(item.override?.highlightTitle || item.override?.highlightText || item.intro) && (
                                        <div className={styles.highlightBox}>
                                            <span className={styles.highlightLabel}>
                                                {item.override?.highlightTitle || 'À savoir'}
                                            </span>
                                            <p>{item.override?.highlightText || item.intro}</p>
                                        </div>
                                    )}

                                    <div className={styles.inlineInfo}>
                                        {typeof item.product.capacityPerSlot === 'number' ? (
                                            <span className={styles.infoPill}>
                                                {item.product.capacityPerSlot} {labels.peopleSuffix}
                                            </span>
                                        ) : null}

                                        {item.product.type ? (
                                            <span className={styles.infoPill}>{item.product.type}</span>
                                        ) : null}

                                        {labels.infoPillFallback ? (
                                            <span className={styles.infoPill}>{labels.infoPillFallback}</span>
                                        ) : null}

                                        <Link
                                            href={item.bookingHref}
                                            className={styles.secondaryBtn}
                                            onClick={() => trackMetaBookingEntry(item.bookingHref)}
                                        >
                                            {item.override?.ctaLabel ||
                                                replaceTitleTemplate(
                                                    labels.bookingButtonLabelWithTitle,
                                                    item.title,
                                                    labels.bookingButtonLabel,
                                                )}
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.activityBody}>
                                <div className={styles.richText}>
                                    {item.paragraphs.length ? (
                                        item.paragraphs.map((paragraph, paragraphIndex) => (
                                            <p key={`${item.anchorId}-paragraph-${paragraphIndex}`}>{paragraph}</p>
                                        ))
                                    ) : (
                                        <p>{item.intro || labels.scheduleFallback}</p>
                                    )}
                                </div>

                                <div className={styles.detailColumns}>
                                    <div className={styles.detailCard}>
                                        <h3>{labels.scheduleTitle}</h3>

                                        {item.schedule.length ? (
                                            <ul className={styles.scheduleList}>
                                                {item.schedule.map((day, dayIndex) => (
                                                    <li key={`${item.anchorId}-schedule-${dayIndex}`}>
                                                        <span>{day.title}</span>
                                                        <strong>
                                                            {day.slots
                                                                .map((slot) =>
                                                                    slot.endTime
                                                                        ? `${slot.startTime} – ${slot.endTime}`
                                                                        : slot.startTime,
                                                                )
                                                                .join(' • ')}
                                                        </strong>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className={styles.muted}>{labels.scheduleFallback}</p>
                                        )}
                                    </div>

                                    <div className={styles.detailCard}>
                                        <h3>{labels.usefulInfoTitle}</h3>

                                        <ul className={styles.bulletList}>
                                            {item.closedDays ? (
                                                <li>
                                                    <strong>{labels.usefulInfoClosedDatesLabel}</strong> {item.closedDays}
                                                </li>
                                            ) : null}

                                            {item.usefulInfo.map((info, infoIndex) => (
                                                <li key={`${item.anchorId}-info-${infoIndex}`}>{info}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>

                                <div className={styles.bottomActions}>
                                    <a href={`#${slice.anchor || 'ateliers-activities'}`} className={styles.textLink}>
                                        {labels.backToTopLabel}
                                    </a>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}